import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { BookOpen, GraduationCap, Shield, Star, Flag, ChevronRight, ChevronDown, ChevronUp, Users, Target, Clock } from "lucide-react";

const Categories = () => {
  const [categoriesData, setCategoriesData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedSTAAR, setExpandedSTAAR] = useState(true);
  const [expandedASVAB, setExpandedASVAB] = useState(true);
  const [expandedSAT, setExpandedSAT] = useState(true);
  const [expandedACT, setExpandedACT] = useState(true);
  const [expandedGrades, setExpandedGrades] = useState({});

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const data = await api.getCategoriesByTrack();
      setCategoriesData(data);
    } catch (error) {
      console.error("Failed to fetch categories:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleGrade = (gradeId) => {
    setExpandedGrades(prev => ({ ...prev, [gradeId]: !prev[gradeId] }));
  };

  const categoryConfig = {
    sat: { bgColor: "bg-indigo-500", lightBg: "bg-indigo-50", borderColor: "border-indigo-200", shadowColor: "shadow-[0_6px_0_0_#4f46e5]", emoji: "📚" },
    act: { bgColor: "bg-lime-500", lightBg: "bg-lime-50", borderColor: "border-lime-200", shadowColor: "shadow-[0_6px_0_0_#65a30d]", emoji: "🎓" },
    asvab: { bgColor: "bg-orange-500", lightBg: "bg-orange-50", borderColor: "border-orange-200", shadowColor: "shadow-[0_6px_0_0_#ea580c]", emoji: "🛡️" },
    citizenship: { bgColor: "bg-red-500", lightBg: "bg-red-50", borderColor: "border-red-200", shadowColor: "shadow-[0_6px_0_0_#dc2626]", emoji: "🇺🇸" }
  };

  const getSubjectColor = (subject) => {
    const colors = {
      "Math": "bg-blue-500",
      "Reading": "bg-green-500",
      "Writing": "bg-purple-500",
      "Science": "bg-orange-500",
      "Social Studies": "bg-amber-500",
      "Algebra I": "bg-blue-600",
      "English I": "bg-emerald-500",
      "English II": "bg-teal-500",
      "Biology": "bg-lime-500",
      "U.S. History": "bg-red-500",
      "General Science": "bg-emerald-500",
      "Arithmetic Reasoning": "bg-orange-500",
      "Word Knowledge": "bg-violet-500",
      "Paragraph Comprehension": "bg-sky-500",
      "Mathematics Knowledge": "bg-indigo-500",
      "Electronics Information": "bg-yellow-500",
      "Auto & Shop Information": "bg-slate-500",
      "Mechanical Comprehension": "bg-red-500",
      "Assembling Objects": "bg-pink-500",
      "Verbal Expression": "bg-lime-500",
      // SAT subtests
      "Reading & Writing": "bg-sky-500",
      "Algebra": "bg-indigo-500",
      "Advanced Math": "bg-violet-600",
      "Problem Solving & Data": "bg-orange-500",
      "Geometry & Trigonometry": "bg-emerald-500",
      "Writing & Language": "bg-rose-500",
      // ACT subtests
      "English": "bg-red-500",
    };
    return colors[subject] || "bg-slate-500";
  };

  const CategoryCard = ({ category }) => {
    const config = categoryConfig[category.id] || categoryConfig.sat;
    const progressPercent = (category.completed_lessons / category.total_lessons) * 100;

    return (
      <Link
        to={`/lesson/${category.id}`}
        data-testid={`category-${category.id}`}
        className={`bg-white rounded-2xl border-2 ${config.borderColor} ${config.shadowColor} p-4 hover:shadow-lg hover:border-indigo-300 transition-all group sm:p-5`}
      >
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 ${config.bgColor} rounded-xl flex items-center justify-center text-xl shadow-lg flex-shrink-0`}>
            {config.emoji}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-heading font-bold text-base text-slate-800">{category.name}</h3>
              <span className={`px-2 py-0.5 ${config.lightBg} text-slate-600 text-xs font-medium rounded-full`}>
                {category.total_lessons} lessons
              </span>
            </div>
            <p className="text-slate-500 text-sm mt-0.5 line-clamp-1">{category.description}</p>
            <div className="mt-2">
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div className={`h-full ${config.bgColor} rounded-full transition-all`} style={{ width: `${progressPercent}%` }} />
              </div>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-slate-500 group-hover:translate-x-1 transition-all flex-shrink-0" />
        </div>
      </Link>
    );
  };

  const SubjectCard = ({ category }) => {
    const isSpanish = category.language === "Spanish";
    const subjectColor = getSubjectColor(category.subject);
    
    return (
      <Link
        to={`/lesson/${category.id}`}
        data-testid={`category-${category.id}`}
        className="flex items-center gap-3 p-3 bg-white rounded-xl border-2 border-slate-200 hover:border-sky-300 hover:shadow-md transition-all group"
      >
        <div className={`w-10 h-10 ${subjectColor} rounded-lg flex items-center justify-center text-white text-sm font-bold shadow`}>
          {category.subject?.charAt(0) || "?"}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-slate-800">{category.subject}</span>
            {isSpanish && (
              <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">Español</span>
            )}
          </div>
          <p className="text-xs text-slate-500">{category.total_lessons} lessons</p>
        </div>
        <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500 group-hover:translate-x-1 transition-all" />
      </Link>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-bounce-subtle">
          <div className="w-16 h-16 bg-lime-500 rounded-full flex items-center justify-center">
            <span className="text-2xl font-bold text-white">C</span>
          </div>
        </div>
      </div>
    );
  }

  if (!categoriesData) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Failed to load categories. Please try again.</p>
      </div>
    );
  }

  const { student_tests, adult_tests } = categoriesData;

  return (
    <div className="space-y-8 animate-slide-up pb-8" data-testid="categories-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 rounded-3xl p-6 text-white">
        <h1 className="font-heading font-black text-2xl sm:text-3xl">Choose Your Test</h1>
        <p className="text-indigo-100 mt-2">Practice, get help when stuck, and know when you're ready.</p>
      </div>

      {/* STUDENT TRACK */}
      <section data-testid="student-track">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
            <GraduationCap className="w-5 h-5 text-indigo-600" />
          </div>
          <h2 className="font-heading font-bold text-xl text-slate-800">Student Tests</h2>
        </div>

        {/* College Prep */}
        <div className="mb-6">
          <h3 className="font-medium text-slate-600 mb-3 flex items-center gap-2">
            <span className="w-2 h-2 bg-indigo-500 rounded-full"></span>
            College Preparation
          </h3>
          <div className="grid gap-3 sm:grid-cols-2">
            {student_tests.college_prep.tests.map((cat) => (
              <CategoryCard key={cat.id} category={cat} />
            ))}
          </div>
        </div>

        {/* SAT Sections */}
        {student_tests.sat?.subtests?.length > 0 && (
          <div className="bg-blue-50 rounded-2xl border-2 border-blue-200 overflow-hidden mb-6">
            <button
              onClick={() => setExpandedSAT(!expandedSAT)}
              className="w-full flex items-center justify-between p-4 hover:bg-blue-100 transition-colors"
              data-testid="sat-section-toggle"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center text-white font-bold text-sm shadow-lg">SAT</div>
                <div className="text-left">
                  <h3 className="font-heading font-bold text-lg text-slate-800">SAT Sections</h3>
                  <p className="text-sm text-slate-500">All Digital SAT test sections</p>
                </div>
              </div>
              {expandedSAT ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
            </button>

            {expandedSAT && (
              <div className="p-4 pt-0 grid gap-2 sm:grid-cols-2">
                {student_tests.sat.subtests.map((subtest) => (
                  <Link
                    key={subtest.id}
                    to={`/lesson/${subtest.id}`}
                    data-testid={`category-${subtest.id}`}
                    className="flex items-center gap-3 p-3 bg-white rounded-xl border-2 border-slate-200 hover:border-blue-300 hover:shadow-md transition-all group"
                  >
                    <div className={`w-10 h-10 ${getSubjectColor(subtest.subject)} rounded-lg flex items-center justify-center text-white text-sm font-bold shadow`}>
                      {subtest.subject?.charAt(0) || "?"}
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="font-semibold text-slate-800 text-sm">{subtest.subject}</span>
                      <p className="text-xs text-slate-500 line-clamp-1">{subtest.description}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500 group-hover:translate-x-1 transition-all" />
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ACT Sections */}
        {student_tests.act?.subtests?.length > 0 && (
          <div className="bg-green-50 rounded-2xl border-2 border-green-200 overflow-hidden mb-6">
            <button
              onClick={() => setExpandedACT(!expandedACT)}
              className="w-full flex items-center justify-between p-4 hover:bg-green-100 transition-colors"
              data-testid="act-section-toggle"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-600 rounded-xl flex items-center justify-center text-white font-bold text-sm shadow-lg">ACT</div>
                <div className="text-left">
                  <h3 className="font-heading font-bold text-lg text-slate-800">ACT Sections</h3>
                  <p className="text-sm text-slate-500">All ACT test sections</p>
                </div>
              </div>
              {expandedACT ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
            </button>

            {expandedACT && (
              <div className="p-4 pt-0 grid gap-2 sm:grid-cols-2">
                {student_tests.act.subtests.map((subtest) => (
                  <Link
                    key={subtest.id}
                    to={`/lesson/${subtest.id}`}
                    data-testid={`category-${subtest.id}`}
                    className="flex items-center gap-3 p-3 bg-white rounded-xl border-2 border-slate-200 hover:border-green-300 hover:shadow-md transition-all group"
                  >
                    <div className={`w-10 h-10 ${getSubjectColor(subtest.subject)} rounded-lg flex items-center justify-center text-white text-sm font-bold shadow`}>
                      {subtest.subject?.charAt(0) || "?"}
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="font-semibold text-slate-800 text-sm">{subtest.subject}</span>
                      <p className="text-xs text-slate-500 line-clamp-1">{subtest.description}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500 group-hover:translate-x-1 transition-all" />
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}

        {/* TSI/TSIA2 */}
        {student_tests.tsi?.subtests?.length > 0 && (
          <div className="bg-violet-50 rounded-2xl border-2 border-violet-200 overflow-hidden mb-6">
            <div className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-violet-600 rounded-xl flex items-center justify-center text-white font-bold text-xs shadow-lg">TSI</div>
                <div className="text-left">
                  <h3 className="font-heading font-bold text-lg text-slate-800">TSI/TSIA2</h3>
                  <p className="text-sm text-slate-500">Texas Success Initiative Assessment</p>
                </div>
              </div>
            </div>
            <div className="px-4 pb-2">
              <p className="text-xs text-violet-700 bg-violet-100 rounded-lg px-3 py-2 italic">Required for most Texas college students before enrolling in credit courses.</p>
            </div>
            <div className="p-4 pt-2 grid gap-2 sm:grid-cols-2">
              {student_tests.tsi.subtests.map((subtest) => (
                <Link
                  key={subtest.id}
                  to={`/lesson/${subtest.id}`}
                  data-testid={`category-${subtest.id}`}
                  className="flex items-center gap-3 p-3 bg-white rounded-xl border-2 border-slate-200 hover:border-violet-300 hover:shadow-md transition-all group"
                >
                  <div className={`w-10 h-10 ${getSubjectColor(subtest.subject)} rounded-lg flex items-center justify-center text-white text-sm font-bold shadow`}>
                    {subtest.subject?.charAt(0) || "?"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="font-semibold text-slate-800 text-sm">{subtest.subject}</span>
                    <p className="text-xs text-slate-500 line-clamp-1">{subtest.description}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500 group-hover:translate-x-1 transition-all" />
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Career & Military */}
        {student_tests.career.tests.length > 0 && (
          <div className="mb-6">
            <h3 className="font-medium text-slate-600 mb-3 flex items-center gap-2">
              <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
              Career & Military
            </h3>
            <div className="grid gap-3 sm:grid-cols-2">
              {student_tests.career.tests.map((cat) => (
                <CategoryCard key={cat.id} category={cat} />
              ))}
            </div>
          </div>
        )}

        {/* ASVAB Subtests */}
        {student_tests.asvab?.subtests?.length > 0 && (
          <div className="bg-orange-50 rounded-2xl border-2 border-orange-200 overflow-hidden mb-6">
            <button
              onClick={() => setExpandedASVAB(!expandedASVAB)}
              className="w-full flex items-center justify-between p-4 hover:bg-orange-100 transition-colors"
              data-testid="asvab-section-toggle"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-500 rounded-xl flex items-center justify-center text-xl shadow-lg">🛡️</div>
                <div className="text-left">
                  <h3 className="font-heading font-bold text-lg text-slate-800">ASVAB Subtests</h3>
                  <p className="text-sm text-slate-500">All 10 Armed Services Vocational Aptitude Battery sections</p>
                </div>
              </div>
              {expandedASVAB ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
            </button>

            {expandedASVAB && (
              <div className="p-4 pt-0">
                <p className="text-xs text-orange-700 bg-orange-100 rounded-lg px-3 py-2 mb-3 italic" data-testid="afqt-note">AR, WK, PC, and MK make up your AFQT score — the score every military branch uses to determine if you qualify to enlist.</p>
                <div className="grid gap-2 sm:grid-cols-2">
                  {student_tests.asvab.subtests.map((subtest) => (
                    <Link
                      key={subtest.id}
                      to={`/lesson/${subtest.id}`}
                      data-testid={`category-${subtest.id}`}
                      className="flex items-center gap-3 p-3 bg-white rounded-xl border-2 border-slate-200 hover:border-orange-300 hover:shadow-md transition-all group"
                    >
                      <div className={`w-10 h-10 ${getSubjectColor(subtest.subject)} rounded-lg flex items-center justify-center text-white text-sm font-bold shadow`}>
                        {subtest.subject?.charAt(0) || "?"}
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="font-semibold text-slate-800 text-sm">{subtest.subject}</span>
                        <p className="text-xs text-slate-500 line-clamp-1">{subtest.description}</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500 group-hover:translate-x-1 transition-all" />
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* STAAR Tests - Grouped by Grade */}
        <div className="bg-sky-50 rounded-2xl border-2 border-sky-200 overflow-hidden">
          <button
            onClick={() => setExpandedSTAAR(!expandedSTAAR)}
            className="w-full flex items-center justify-between p-4 hover:bg-sky-100 transition-colors"
            data-testid="staar-section-toggle"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-sky-500 rounded-xl flex items-center justify-center text-xl shadow-lg">⭐</div>
              <div className="text-left">
                <h3 className="font-heading font-bold text-lg text-slate-800">STAAR Tests</h3>
                <p className="text-sm text-slate-500">State of Texas Assessments of Academic Readiness</p>
              </div>
            </div>
            {expandedSTAAR ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
          </button>

          {expandedSTAAR && (
            <div className="p-4 pt-0 space-y-3">
              {student_tests.staar.grades?.map((grade) => (
                <div key={grade.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <button
                    onClick={() => toggleGrade(grade.id)}
                    className="w-full flex items-center justify-between p-3 hover:bg-slate-50 transition-colors"
                    data-testid={`grade-toggle-${grade.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-sky-100 rounded-lg flex items-center justify-center">
                        <span className="text-sky-600 font-bold text-sm">
                          {grade.label.includes("EOC") ? "HS" : grade.label.replace("Grade ", "")}
                        </span>
                      </div>
                      <span className="font-semibold text-slate-800">{grade.label}</span>
                      <span className="text-xs text-slate-400">{grade.subjects.length} subjects</span>
                    </div>
                    {expandedGrades[grade.id] ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                  </button>
                  
                  {expandedGrades[grade.id] && (
                    <div className="p-3 pt-0 grid gap-2 sm:grid-cols-2">
                      {grade.subjects.map((subject) => (
                        <SubjectCard key={subject.id} category={subject} />
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ADULT TRACK */}
      <section data-testid="adult-track" className="pt-4">
        <div className="border-t-2 border-dashed border-slate-200 pt-6">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-red-600" />
            </div>
            <h2 className="font-heading font-bold text-xl text-slate-800">U.S. Citizenship Test Prep</h2>
          </div>
          <p className="text-slate-500 text-sm mb-4">Designed for parents and adult learners</p>
          
          <div className="grid gap-3">
            {adult_tests.tests.map((cat) => (
              <CategoryCard key={cat.id} category={cat} />
            ))}
          </div>
        </div>
      </section>

      {/* Quick Actions */}
      <section className="grid sm:grid-cols-2 gap-4">
        <Link
          to="/study-plans"
          className="bg-gradient-to-r from-lime-500 to-green-600 rounded-2xl p-5 text-white hover:from-lime-600 hover:to-green-700 transition-all group"
          data-testid="study-plans-link"
        >
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Target className="w-6 h-6" />
            </div>
            <div>
              <h3 className="font-heading font-bold text-lg">Study Plans</h3>
              <p className="text-lime-100 text-sm">Set goals & track progress</p>
            </div>
            <ChevronRight className="w-5 h-5 ml-auto opacity-70 group-hover:translate-x-1 transition-transform" />
          </div>
        </Link>

        <Link
          to="/mock-test/sat"
          className="bg-gradient-to-r from-indigo-500 to-indigo-600 rounded-2xl p-5 text-white hover:from-indigo-600 hover:to-indigo-700 transition-all group"
          data-testid="mock-test-link"
        >
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Clock className="w-6 h-6" />
            </div>
            <div>
              <h3 className="font-heading font-bold text-lg">Mock Tests</h3>
              <p className="text-indigo-100 text-sm">Timed practice exams</p>
            </div>
            <ChevronRight className="w-5 h-5 ml-auto opacity-70 group-hover:translate-x-1 transition-transform" />
          </div>
        </Link>
      </section>

      {/* Tips Card */}
      <div className="bg-indigo-50 rounded-2xl p-5 border-2 border-indigo-100">
        <h3 className="font-heading font-bold text-lg text-indigo-800 mb-2">💡 Pro Tip</h3>
        <p className="text-indigo-600">
          Focus on one test at a time for best results. Complete at least 5 questions daily to maintain your streak and track your Exam Readiness!
        </p>
      </div>
    </div>
  );
};

export default Categories;
